import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FaBell } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'

import { mark_read, mark_all_read } from '../../store/reducers/notificationReducer'

// Polished notification dropdown.
// All dispatches (mark_read, mark_all_read) preserved.
//
// `belongsToUser` filter is passed from Headers to keep the legacy
// SELLER_ONLY_NOTIFICATION_TYPES rule centralised.

const TYPE_ICON = {
    order_new:               '🛍️',
    order_sent:              '📦',
    order_confirmed:         '✅',
    order_cancelled:         '❌',
    chat_message:            '💬',
    auto_confirmed:          '🤖',
    delivery_reminder:       '⚠️',
}
const TYPE_BG = {
    order_new:               'bg-brand-50 text-brand-700',
    order_sent:              'bg-accent-50 text-accent-700',
    order_confirmed:         'bg-success-50 text-success-700',
    order_cancelled:         'bg-danger-50 text-danger-700',
    chat_message:            'bg-brand-50 text-brand-700',
    auto_confirmed:          'bg-success-50 text-success-700',
    delivery_reminder:       'bg-warning-50 text-warning-700',
}

const timeAgo = (date) => {
    const d = (Date.now() - new Date(date)) / 1000
    if (d < 60)    return 'indi'
    if (d < 3600)  return `${Math.floor(d / 60)} dəq`
    if (d < 86400) return `${Math.floor(d / 3600)} saat`
    return `${Math.floor(d / 86400)} gün`
}

const NotificationPanel = ({ visible, onClose, belongsToUser }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { userInfo }   = useSelector(s => s.auth)
    const { notifications = [], loading } = useSelector(s => s.notification)

    if (!visible) return null

    const visibleNotifications = (notifications || []).filter(n => belongsToUser?.(n, userInfo))
    const visibleUnreadCount   = visibleNotifications.filter(n => !n.isRead).length

    const handleClick = (n) => {
        if (!n.isRead) dispatch(mark_read(n._id))
        if (n.link) navigate(n.link)
        onClose?.()
    }

    return (
        <div
            className='absolute right-0 top-[calc(100%+8px)] z-[99999] w-[360px] sm:w-[320px] overflow-hidden rounded-card border border-slate-200 bg-white shadow-card-hover'
            role='dialog'
            aria-label='Bildirişlər'
        >
            {/* Header */}
            <div className='flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-br from-brand-50 to-white px-4 py-3'>
                <div className='flex items-center gap-2'>
                    <span className='inline-flex h-7 w-7 items-center justify-center rounded-card bg-brand-100 text-brand-700'>
                        <FaBell size={12} />
                    </span>
                    <span className='text-sm font-bold text-slate-800'>Bildirişlər</span>
                    {visibleUnreadCount > 0 && (
                        <span className='inline-flex items-center rounded-pill bg-danger-600 px-1.5 py-0.5 text-[10px] font-bold text-white'>
                            {visibleUnreadCount > 99 ? '99+' : visibleUnreadCount}
                        </span>
                    )}
                </div>
                <div className='flex items-center gap-3'>
                    {visibleUnreadCount > 0 && (
                        <button
                            type='button'
                            onClick={() => dispatch(mark_all_read())}
                            className='text-[11px] font-semibold text-brand-700 hover:text-brand-800 hover:underline'
                        >
                            Hamısını oxu
                        </button>
                    )}
                    <button
                        type='button'
                        onClick={onClose}
                        aria-label='Bağla'
                        className='inline-flex h-6 w-6 items-center justify-center rounded-pill bg-slate-100 text-slate-500 hover:bg-slate-200'
                    >
                        <MdClose size={13} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className='overflow-y-auto' style={{ maxHeight: 380 }}>
                {loading && (
                    <div className='flex flex-col items-center justify-center gap-3 py-10'>
                        <div
                            className='h-8 w-8 animate-spin rounded-full border-brand-200 border-t-brand-600'
                            style={{ borderWidth: 3, borderStyle: 'solid' }}
                        />
                        <p className='text-xs text-slate-400'>Yüklənir…</p>
                    </div>
                )}

                {!loading && visibleNotifications.length === 0 && (
                    <div className='flex flex-col items-center justify-center gap-2 px-6 py-12 text-center'>
                        <FaBell size={28} className='text-brand-200' />
                        <p className='text-sm font-semibold text-slate-700'>Bildiriş yoxdur</p>
                        <p className='text-xs text-slate-400'>
                            Sifariş, çatdırılma və sistem bildirişləriniz burada görünəcək.
                        </p>
                    </div>
                )}

                {!loading && visibleNotifications.map(n => (
                    <button
                        type='button'
                        key={n._id}
                        onClick={() => handleClick(n)}
                        className={`group flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50 ${
                            !n.isRead ? 'bg-brand-50/40' : ''
                        }`}
                    >
                        <span
                            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-card text-base transition-transform group-hover:scale-105 ${
                                n.isRead ? 'bg-slate-100 text-slate-500' : (TYPE_BG[n.notifType] || 'bg-brand-50 text-brand-700')
                            }`}
                        >
                            {TYPE_ICON[n.notifType] || '🔔'}
                        </span>
                        <div className='min-w-0 flex-1'>
                            <p className={`text-[13px] leading-snug ${!n.isRead ? 'font-bold text-slate-800' : 'font-semibold text-slate-600'}`}>
                                {n.title}
                            </p>
                            {n.message && (
                                <p className='mt-0.5 line-clamp-2 text-[11px] text-slate-500'>{n.message}</p>
                            )}
                            <p className='mt-1 text-[10px] text-slate-400'>{timeAgo(n.createdAt)}</p>
                        </div>
                        {!n.isRead && (
                            <span className='mt-2 inline-flex h-2 w-2 shrink-0 animate-pulse rounded-pill bg-brand-600' />
                        )}
                    </button>
                ))}
            </div>

            {/* Footer */}
            {visibleNotifications.length > 0 && (
                <div className='border-t border-slate-100'>
                    <Link
                        to='/dashboard/notifications'
                        onClick={onClose}
                        className='flex items-center justify-center gap-1.5 py-3 text-xs font-semibold text-brand-700 transition-colors hover:bg-brand-50'
                    >
                        Bütün bildirişlərə bax →
                    </Link>
                </div>
            )}
        </div>
    )
}

export default NotificationPanel
